import { useEffect, useState, useRef, type RefObject } from "react";

/**
 * Timeline item metadata (no refs, just descriptive data)
 * Refs are managed separately via callback refs in the parent component
 */
export interface TimelineItem {
  type: "optional" | "main";
  hasBullet: boolean;
}

export interface MeasuredPosition {
  top: number;
  height: number;
  center: number;
}

export interface UseTimelineMeasurementsReturn {
  positions: MeasuredPosition[];
  containerRef: RefObject<HTMLDivElement | null>;
}

/**
 * Custom hook for measuring timeline content positions using ResizeObserver
 *
 * Follows React 19 best practices (2025):
 * - Parent component manages DOM nodes via callback refs
 * - Hook receives nodes array directly (no RefObjects)
 * - Single ResizeObserver instance, properly disconnected on deps change
 * - Dynamic array size (no pre-allocation needed)
 *
 * @param items - Timeline item metadata (type, hasBullet)
 * @param nodes - Actual DOM nodes (from callback refs), same order as items
 * @returns Object with positions array and container ref
 *
 * @see https://react.dev/reference/react/useRef#storing-information-between-renders
 * @see https://legacy.reactjs.org/docs/refs-and-the-dom.html#callback-refs
 */
export function useTimelineMeasurements(
  items: TimelineItem[],
  nodes: (HTMLDivElement | null)[]
): UseTimelineMeasurementsReturn {
  const containerRef = useRef<HTMLDivElement>(null);
  const [positions, setPositions] = useState<MeasuredPosition[]>([]);
  const observerRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Disconnect previous observer if it exists (prevents observer buildup on deps change)
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    // Extract measurement logic to reusable function
    const measure = () => {
      if (!containerRef.current) return null;

      const containerTop = containerRef.current.getBoundingClientRect().top;

      return items.map((item, index) => {
        const node = nodes[index];
        if (!node) return { top: 0, height: 0, center: 0 };

        const rect = node.getBoundingClientRect();
        const relativeTop = rect.top - containerTop;

        return {
          top: relativeTop,
          height: rect.height,
          center: relativeTop + (rect.height / 2)
        };
      });
    };

    // Immediate initial measurement (before observer)
    const initialMeasure = measure();
    if (initialMeasure) {
      setPositions(initialMeasure);
    }

    // Feature detection - fallback if ResizeObserver unavailable
    if (typeof ResizeObserver === "undefined") {
      // eslint-disable-next-line consistent-return
      return; // Gracefully degrade - initial measurement already done
    }

    const observer = new ResizeObserver(() => {
      const measured = measure();
      if (measured) {
        // Shallow compare to prevent unnecessary re-renders
        setPositions(prev => {
          if (prev.length !== measured.length) return measured;

          const hasChanged = measured.some((pos, i) =>
            pos.top !== prev[i]?.top ||
            pos.height !== prev[i]?.height ||
            pos.center !== prev[i]?.center
          );

          return hasChanged ? measured : prev;
        });
      }
    });

    // Store observer reference for cleanup
    observerRef.current = observer;

    // Observe the container itself
    observer.observe(containerRef.current);

    // Observe all content nodes
    nodes.forEach(node => {
      if (node) {
        observer.observe(node);
      }
    });

    // Cleanup: disconnect observer when component unmounts or deps change
    // eslint-disable-next-line consistent-return
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [items, nodes]); // Re-setup observers when items or nodes change

  return { positions, containerRef };
}
